"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { User, Lock } from "lucide-react"
import authService from "../../services/authService"

interface RecentAccount {
    id: string
    username: string
    name: string
    lastLogin: string
    profileImage?: string
}

interface AccountSwitcherProps {
    onClose: () => void
    currentUserProfile?: string | null
}

export function AccountSwitcher({ onClose, currentUserProfile }: AccountSwitcherProps) {
    const [recentAccounts, setRecentAccounts] = useState<RecentAccount[]>([])
    const [selectedAccount, setSelectedAccount] = useState<RecentAccount | null>(null)
    const [password, setPassword] = useState("")
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)


    useEffect(() => {
        // Load recent accounts
        const accounts = authService.getRecentAccounts();

        // Filter out accounts without usernames
        const validAccounts = accounts.filter(account =>
            account.username && account.username.trim() !== ""
        );

        setRecentAccounts(validAccounts);
    }, [currentUserProfile]);


    const handleAccountSelect = (account: RecentAccount) => {
                setSelectedAccount(account)
        setError(null)
    }

    const handleSwitchAccount = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedAccount) return;

        setIsLoading(true);
        setError(null);

        try {
            // Validate username
            if (!selectedAccount.username || selectedAccount.username.trim() === "") {
                throw new Error("Le nom d'utilisateur est requis");
            }

            // Attempt to login with the selected account
            await authService.login({
                username: selectedAccount.username,
                password,
            });

            // If successful, reload the page to reflect the new user
            window.location.reload();
        } catch (err) {
                        setError(err instanceof Error ? err.message : "Échec de la connexion. Veuillez vérifier votre mot de passe.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleBackToList = () => {
        setSelectedAccount(null)
        setPassword("")
        setError(null)
    }

    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            style={{ backdropFilter: "blur(2px)" }}
        >
            <div
                className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold flex items-center text-primary">
                        <User className="w-5 h-5 mr-2 g-primary" />
                        {selectedAccount ? "Entrez votre mot de passe" : "Changer de compte"}
                    </h2>
                </div>

                {!selectedAccount ? (
                    <div className="p-4">
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Select an account to switch to:</p>

                        {recentAccounts.length === 0 ? (
                            <div className="text-center py-6 text-neutral-500 dark:text-neutral-400">
                                <p>Aucun compte récent trouvé</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {recentAccounts.map((account) => (
                                    <button
                                        key={account.id}
                                        className="w-full flex items-center text-primary p-3 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors text-left"
                                        onClick={() => handleAccountSelect(account)}
                                    >
                                        <div className="w-10 h-10 rounded-full overflow-hidden bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mr-3 flex-shrink-0">
                                            {account.profileImage ? (
                                                <img
                                                    src={account.profileImage || "/placeholder.svg"}
                                                    alt={account.name}
                                                    className="h-full w-full object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = "none"
                                                        e.currentTarget.parentElement!.innerHTML =
                                                            `<span class="text-emerald-600 dark:text-emerald-400 font-medium text-lg">${account.name.charAt(0).toUpperCase()}</span>`
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-emerald-600 dark:text-emerald-400 font-medium text-lg">
                                                    {account.name.charAt(0).toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{account.name}</p>
                                            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                                                {account.username || "(Pas de nom d'utilisateur)"}
                                            </p>
                                        </div>
                                        <span className="text-xs text-neutral-400 dark:text-neutral-500">{new Date(account.lastLogin).toLocaleDateString()}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                            <button onClick={onClose} className="w-full py-2 px-4 secondary-button transition-colors">
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSwitchAccount} className="p-4">
                        <div className="flex items-center mb-4">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mr-3">
                                {selectedAccount.profileImage ? (
                                    <img
                                        src={selectedAccount.profileImage || "/placeholder.svg"}
                                        alt={selectedAccount.name}
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none"
                                            e.currentTarget.parentElement!.innerHTML =
                                                `<span class="g-primary font-medium text-lg">${selectedAccount.name.charAt(0).toUpperCase()}</span>`
                                        }}
                                    />
                                ) : (
                                    <span className="text-primary font-medium text-lg">
                                        {selectedAccount.name.charAt(0).toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <div>
                                <p className="font-medium text-primary">{selectedAccount.name}</p>
                                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                    {selectedAccount.username || "(Pas de nom d'utilisateur)"}
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-600 dark:text-red-400">
                                {error}
                            </div>
                        )}

                        <div className="mb-4">
                            <label htmlFor="password" className="block text-sm text-primary font-medium mb-1">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-neutral-800 dark:text-white"
                                    placeholder="Enter password"
                                    required
                                />
                                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary" />
                            </div>
                        </div>

                        <div className="flex space-x-2">

                            <button
                                type="button"
                                onClick={handleBackToList}
                                className="flex-1 py-2 px-4 dark:text-white bg-neutral-100 dark:bg-neutral-700 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
                                disabled={isLoading}
                            >
                                Back
                            </button>

                            <button
                                type="submit"
                                className="flex-1 py-2 px-4 primary-button disabled:opacity-50"
                                disabled={isLoading || !password}
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center">
                                        Switching...
                                    </span>
                                ) : (
                                    <span className="flex justify-around items-center" >
                                        <User className="w-4 h-4 mr-2 g-primary" />
                                        Switch Account
                                        </span>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}